/**
 * Vuex store modules
 */
import Vuex from 'vuex';
import createLogger from 'vuex/dist/logger.js';
import createPersistedState from 'vuex-persistedstate';
import SecureLS from 'secure-ls';

import createStore from '@/store/index.js';
import { isProdSite, isLocalHost, isTestEnv } from '@/helpers/deployment.js';

export const plugin = {
    name: 'vuex store',

    pluginProperties: {
        store: null
    },

    install (Vue) {
        Vue.use(Vuex);

        const plugins = [];
        let storage;

        // encrypt localStorage in production
        // https://github.com/softvar/secure-ls
        if (isProdSite) {
            const secureStorage = new SecureLS({ isCompression: true });
            storage = {
                getItem: key => secureStorage.get(key),
                setItem: (key, value) => secureStorage.set(key, value),
                removeItem: key => secureStorage.remove(key)
            };
        }

        if (!isTestEnv) {
            plugins.push(
                /**
                 * Vuex store persistence to localStorage
                 */
                createPersistedState({
                    // key <String>: The key to store the persisted state under. (default: vuex)
                    key: 'main',
                    // paths <Array>: An array of any paths to partially persist the state. If no paths are given, the complete state is persisted. Paths must be specified using dot notation. If using modules, include the module name. eg: "auth.user" (default: [])
                    // reducer <Function>: A function that will be called to reduce the state to persist based on the given paths. Defaults to include the values.
                    // subscriber <Function>: A function called to setup mutation subscription. Defaults to store => handler => store.subscribe(handler)
                    // storage <Object>: Instead for (or in combination with) getState and setState. Defaults to localStorage.
                    storage
                    // getState <Function>: A function that will be called to rehydrate a previously persisted state. Defaults to using storage.
                    // setState <Function>: A function that will be called to persist the given state. Defaults to using storage.
                    // filter <Function>: A function that will be called to filter any mutations which will trigger setState on storage eventually. Defaults to () => true
                    // arrayMerger <Function>: A function for merging arrays when rehydrating state. Defaults to function (store, saved) { return saved } (saved state replaces supplied state).
                    // rehydrated <Function>: A function that will be called when the rehydration is finished. Useful when you are using Nuxt.js, which the rehydration of the persisted state happens asynchronously. Defaults to store => {}
                })
            );
        }

        // add logger for development
        if (isLocalHost) {
            plugins.push(createLogger());
        }

        this.pluginProperties.store = createStore(Vue, {
            plugins,
            strict: isLocalHost // not in production!
        });

        // store is passed to Vue app constructor and made global
    }
};

export default plugin;
